/**
 * <steward-thinking> — Vertical "thinking" timeline.
 *
 * Renders the agent's interim phases (knowledge-base retrieval, LLM
 * reasoning iterations, tool calls + results) as a Claude-style vertical
 * timeline above the streaming assistant bubble.
 *
 * Steps flow in from the host via the `steps` property (assigned by
 * `<steward-chat>` as `thinking` SSE events arrive). The `active` boolean
 * controls the pulsing-dot animation on the most-recent step and the
 * header label ("Thinking…" vs. "Thought · N steps").
 *
 * The timeline defaults to collapsed. It auto-expands while a turn is
 * active (live) so the user watches the agent work, and auto-collapses
 * when `active` flips to false (the assistant's final tokens have
 * flushed). A completed or reloaded timeline mounts with active=false
 * and stays collapsed. The header remains clickable to toggle manually.
 */
import { LitElement, html, css, nothing, type PropertyValues } from 'lit';
import { property, state } from 'lit/decorators.js';
import { sharedStyles } from './steward-styles.js';
import { defineOnce } from './register.js';

/** Mirror of the backend SSE `thinking` event payload — see
 *  `steward-stream.ts` for the canonical type. Duplicated here only to
 *  keep this module standalone-importable for tests. */
export interface ThinkingStep {
  step: number;
  /** The kinds the widget knows, and any other: the set is open, and a kind
   *  the widget does not know is shown by its label. */
  kind: 'retrieving' | 'synthesizing' | 'tool_call' | 'tool_result' | 'tool_error' | 'responding' | (string & {});
  label: string;
  tool?: string;
  args?: string;
  /** Server-stamped elapsed-at-emission (ms since the turn began). A step's
   *  duration is rendered as the diff to the next step's elapsedMs. */
  elapsedMs?: number;
}

@defineOnce('steward-thinking')
export class StewardThinking extends LitElement {
  /** Ordered list of steps the agent has completed so far. Assigning a
   *  new array (immutable update) triggers re-render; mutating in place
   *  does NOT (standard Lit reactivity). */
  @property({ attribute: false }) steps: ThinkingStep[] = [];

  /** True while the assistant is still streaming. Drives the pulsing
   *  dot on the most-recent step + the "Thinking…" header label. When
   *  the parent flips this false (typically on `steward-message` /
   *  done), the timeline collapses to the header. */
  @property({ type: Boolean, reflect: true }) active = false;

  /** Set while a server-side cancel is in flight for this (still-active) turn.
   *  Flips the header label to "Stopping…" and freezes the active-step pulse so
   *  the timeline stops contradicting the user's Stop click. Reflected so the
   *  pulse-freeze can be expressed in CSS. */
  @property({ type: Boolean, reflect: true }) cancelling = false;

  /** Live-render mode — governs ONLY the in-progress (active) render of the
   *  timeline; the completed/inactive trace is identical across modes (shared
   *  convergence path):
   *  - `full`  — the growing vertical timeline (header + all steps). Default.
   *  - `step`  — only the most-recent step as a single line that replaces in
   *              place as the agent advances, with its live counting-up duration.
   *  - `hidden`— nothing while active (the bubble's typing indicator signals
   *              "working"); no header, no steps, no live timer.
   *  Once `active` flips false every mode renders the same collapsed summary. */
  @property({ type: String }) mode: 'full' | 'step' | 'hidden' = 'full';

  /** Header expand/collapse state. Defaults to collapsed. Auto-expands
   *  while a turn is active (live) so the user sees the agent working;
   *  auto-collapses when the turn ends. Click the header to toggle
   *  manually within a session. */
  @state() private _expanded = false;

  /** Turn total (ms) for the collapsed-header aggregate, used only when there
   *  is no `responding` step to read delivery-start from (cancel/timeout). On
   *  a normal turn the `responding` step's elapsedMs is the agent total and
   *  this is ignored. */
  @property({ type: Number, attribute: false }) totalMs?: number;

  /** Live per-step timer. While a non-`responding` step is the active/last
   *  one, this ticks ~1s and forces a re-render so the active step's duration
   *  counts up. Cleared when the turn ends and in disconnectedCallback (the
   *  child is torn down mid-stream when the host starts a new conversation) —
   *  a single-instance guard in _startTimer prevents stacking across the many
   *  willUpdate passes a streaming turn triggers. */
  private _timer: ReturnType<typeof setInterval> | null = null;
  /** `step` number of the step currently being timed live, and the client
   *  clock when it became active — the live duration is `now - _activeStepStart`. */
  private _activeStepNumber = -1;
  private _activeStepStart = 0;
  /** The active step's live duration frozen at the moment the turn ended with
   *  no successor step (cancel/timeout — no `responding` follows). null when a
   *  successor exists (the server-authoritative diff is shown instead). */
  private _frozenLiveMs: number | null = null;

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this._stopTimer();
  }

  private _startTimer(): void {
    if (this._timer !== null) return; // single-instance guard
    this._timer = setInterval(() => this.requestUpdate(), 1000);
  }

  private _stopTimer(): void {
    if (this._timer !== null) {
      clearInterval(this._timer);
      this._timer = null;
    }
  }

  static styles = [
    sharedStyles,
    css`
      :host {
        display: block;
        margin: 0 0 12px 0;
        font-size: 12px;
      }

      /* Theme-neutral gray scale, derived from --steward-text via
       * color-mix so it adapts to both light and dark hosts without
       * picking up the palette's accent (soleum teal, lux blue, etc.).
       * In light mode --steward-text is near-black, so 55% mix reads
       * as a quiet mid-gray; in dark mode --steward-text is near-white,
       * so the same 55% mix reads as a soft pale gray. Both are
       * visually equivalent and neutral. */
      .header {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 2px 4px;
        margin: 0 0 4px -4px;
        border-radius: 4px;
        cursor: pointer;
        color: color-mix(in srgb, var(--steward-text) 55%, transparent);
        font-weight: 500;
        user-select: none;
        background: transparent;
        border: none;
        font-family: inherit;
        font-size: inherit;
      }
      .header:hover {
        color: color-mix(in srgb, var(--steward-text) 80%, transparent);
      }
      .header:focus-visible {
        outline: 2px solid var(--steward-signature);
        outline-offset: 2px;
      }
      .chevron {
        width: 12px;
        height: 12px;
        transition: transform 0.15s ease;
        flex-shrink: 0;
      }
      .chevron.open {
        transform: rotate(90deg);
      }

      /* The timeline doesn't render the rule itself — each .step is
       * responsible for drawing a connector to the NEXT step's dot
       * (via ::after) so we can leave a small gap at each end and
       * never weld the rule to a dot's circumference. The last step
       * has no connector because nothing follows it. */
      .timeline {
        list-style: none;
        margin: 0;
        padding: 4px 0 4px 18px;
        margin-left: 5px;
      }
      .step {
        position: relative;
        padding: 3px 0;
      }
      .step::before {
        content: '';
        position: absolute;
        left: -22px;
        top: 9px;
        width: 5px;
        height: 5px;
        border-radius: 50%;
        background: color-mix(in srgb, var(--steward-text) 35%, transparent);
      }
      /* Per-pair connector — a 2px segment from below THIS dot to
       * above the NEXT dot, with a 4px breathing gap at each end so
       * the segment never touches a dot's circumference. The dot
       * center is at x = -22 + 2.5 = -19.5; the segment is 2px wide
       * centred on that x → left = -20.5. */
      .step:not(:last-child)::after {
        content: '';
        position: absolute;
        left: -20.5px;
        /* Starts 4px below this dot's bottom edge (dot bottom is at
         * top:9 + height:5 = 14, so segment top = 18). */
        top: 18px;
        /* Ends 4px above the next dot's top edge. Next dot lives in
         * the next .step at top:9 of that step's box; this step
         * extends past its own bottom by 5px so the segment stops at
         * y = 9 - 4 = 5 of the next .step → bottom: -5. */
        bottom: -5px;
        width: 2px;
        background: color-mix(in srgb, var(--steward-text) 18%, transparent);
        border-radius: 1px;
      }
      .step.active::before {
        background: var(--steward-signature);
        width: 6px;
        height: 6px;
        top: 8.5px;
        left: -22.5px;
        animation: thinkingPulse 1.4s ease-in-out infinite;
      }
      /* Subtle by default — neutral gray derived from text, normal
       * weight. The active step (most-recent while streaming) lifts to
       * full text color so the "live" line reads as the primary cue
       * without raising the volume on the rest. */
      .step-label {
        color: color-mix(in srgb, var(--steward-text) 55%, transparent);
        font-weight: 400;
      }
      .step.active .step-label {
        color: var(--steward-text);
      }
      /* Tool-call steps render only this monospace detail (no label, no
       * highlight) — the label would just repeat the tool name. */
      .step-detail {
        /* Inline (not block) so the per-step duration sits on the SAME line,
         * matching the .step-label rows; a long endpoint still wraps via
         * word-break and the duration trails the last fragment. */
        display: inline;
        font-family: var(--steward-font-mono);
        font-size: 11px;
        color: color-mix(in srgb, var(--steward-text) 55%, transparent);
        word-break: break-all;
      }
      .step.active .step-detail {
        color: var(--steward-text);
      }

      /* Per-step duration — quiet, tabular so the digits don't jitter as the
       * active step's live counter ticks. Sits just after the label/detail. */
      .step-time {
        margin-left: 6px;
        color: color-mix(in srgb, var(--steward-text) 40%, transparent);
        font-variant-numeric: tabular-nums;
        font-size: 11px;
      }
      .step.active .step-time {
        color: color-mix(in srgb, var(--steward-text) 65%, transparent);
      }

      /* step mode: the single most-recent step shown in place of the full
       * timeline while active. Mirrors the active-step volume from the list
       * (full text color) so the live line reads as the primary cue, but with
       * no dot/connector chrome since there's no list to anchor to. */
      .current-step {
        padding: 3px 0;
      }
      .current-step .step-label {
        color: var(--steward-text);
        font-weight: 400;
      }
      .current-step .step-detail {
        color: var(--steward-text);
      }
      .current-step .step-time {
        color: color-mix(in srgb, var(--steward-text) 65%, transparent);
      }

      @keyframes thinkingPulse {
        0%, 100% { transform: scale(1);   opacity: 1; }
        50%      { transform: scale(1.4); opacity: 0.6; }
      }

      /* Reduced-motion: drop the pulse but keep the color shift so the
       * active step is still distinguishable statically. */
      @media (prefers-reduced-motion: reduce) {
        .step.active::before {
          animation: none;
        }
      }

      /* Cancel in flight: freeze the active-step pulse but keep its color, so the
       * timeline reads as "winding down" rather than still working. */
      :host([cancelling]) .step.active::before {
        animation: none;
      }
    `,
  ];

  protected willUpdate(changed: PropertyValues): void {
    super.willUpdate?.(changed);
    if (changed.has('active')) {
      const prev = changed.get('active');
      if (this.active && prev !== true && this.mode !== 'hidden') {
        // Became active — a live turn (including the first render of a
        // streaming timeline). Expand so the user sees the agent working.
        // In `hidden` mode the active render is empty, so don't auto-expand.
        this._expanded = true;
      } else if (prev === true && this.active === false) {
        // Turn finished — collapse back to the header.
        this._expanded = false;
      }
      // A completed/reloaded timeline mounts with active=false and no
      // true→false transition, so neither branch fires and it stays
      // collapsed (the default) — fixing the all-expanded-on-refresh bug.

      if (prev === true && this.active === false) {
        // Turn ended: freeze the active step's live counter at its last value
        // (shown only for a successor-less last step — cancel/timeout, where no
        // `responding` follows).
        this._frozenLiveMs = this._activeStepStart > 0 ? Date.now() - this._activeStepStart : null;
      }
    }

    // Switching to a visible mode mid-turn must reveal the live timeline — the
    // same intent as the active false→true auto-expand. When only `mode`
    // changes while a turn is active, the `changed.has('active')` block above
    // doesn't run, so a hidden→full / step→full switch would otherwise leave a
    // collapsed live timeline. (`step` renders via _renderCurrentStep and
    // ignores `_expanded`, so this only affects `full`.)
    if (changed.has('mode') && this.active && this.mode !== 'hidden') {
      this._expanded = true;
    }

    // Mark when the current last step became active, so its live counter
    // measures from the right moment.
    if (changed.has('steps')) {
      const last = this.steps[this.steps.length - 1];
      if (last && last.step !== this._activeStepNumber) {
        this._activeStepNumber = last.step;
        this._activeStepStart = Date.now();
        this._frozenLiveMs = null; // a fresh active step supersedes any freeze
      }
    }

    // The live timer runs only while a non-`responding` step is active —
    // `responding` is client-side typewriter playback, not agent work, so it
    // gets no live duration. In `hidden` mode nothing is rendered while active,
    // so don't tick an unseen counter. Start/stop is idempotent; clearing on
    // inactive is what stops a finished timeline from ticking forever.
    const last = this.steps[this.steps.length - 1];
    if (this.active && this.mode !== 'hidden' && last != null && last.kind !== 'responding') {
      this._startTimer();
    } else {
      this._stopTimer();
    }
  }

  /** Duration to display beside a step, in ms, or null for none.
   *  - completed step: server-authoritative diff to the next step's elapsedMs
   *  - active last step (non-responding): live, counting up from when it began
   *  - last step after the turn ended with no successor: the frozen live value
   *  - `responding`: never (its time is client typewriter playback) */
  private _stepDurationMs(step: ThinkingStep, index: number): number | null {
    if (step.kind === 'responding') return null;
    const isLast = index === this.steps.length - 1;
    if (!isLast) {
      const next = this.steps[index + 1];
      if (step.elapsedMs == null || next?.elapsedMs == null) return null;
      return Math.max(0, next.elapsedMs - step.elapsedMs);
    }
    if (this.active) {
      return this._activeStepStart > 0 ? Math.max(0, Date.now() - this._activeStepStart) : null;
    }
    return this._frozenLiveMs;
  }

  /** The turn total for the collapsed header: delivery-start (the `responding`
   *  step's elapsedMs) when present, else the terminal total handed in via
   *  `totalMs` (cancel/timeout). null when neither is known. */
  private _totalMs(): number | null {
    const responding = this.steps.find((s) => s.kind === 'responding');
    return responding?.elapsedMs ?? this.totalMs ?? null;
  }

  private _formatDuration(ms: number): string {
    const s = ms / 1000;
    if (s < 10) return `${s.toFixed(1)}s`;
    if (s < 60) return `${Math.round(s)}s`;
    const m = Math.floor(s / 60);
    return `${m}m ${Math.round(s - m * 60)}s`;
  }

  render() {
    if (this.steps.length === 0) return nothing;

    // Mode gates ONLY the live (active) render. Once the turn is done the
    // shared collapsed-summary path below runs for every mode (convergence).
    if (this.active) {
      if (this.mode === 'hidden') return nothing;
      if (this.mode === 'step') return this._renderCurrentStep();
    }

    const count = this.steps.length;
    const stepsLabel = `${count} ${count === 1 ? 'step' : 'steps'}`;
    // Aggregate turn total — shown only once the turn has finished (the header
    // is the collapsed summary). Agent working time: delivery-start (the
    // `responding` step's elapsedMs), which excludes the client-side typewriter
    // playback; on cancel/timeout (no `responding`) fall back to the terminal
    // total handed in via `totalMs`.
    const total = this.active ? null : this._totalMs();
    const headerLabel = this.active
      ? this.cancelling ? 'Stopping…' : 'Thinking…'
      : total != null
        ? `Thought for ${this._formatDuration(total)} · ${stepsLabel}`
        : `Thought · ${stepsLabel}`;

    return html`
      <button
        type="button"
        class="header"
        @click=${this._toggleExpand}
        aria-expanded="${this._expanded}"
        aria-controls="thinking-timeline"
      >
        <svg
          class="chevron ${this._expanded ? 'open' : ''}"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          aria-hidden="true"
        >
          <path d="M6 4l4 4-4 4" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
        <span>${headerLabel}</span>
      </button>
      ${this._expanded ? this._renderTimeline() : nothing}
    `;
  }

  private _renderTimeline() {
    return html`
      <ul class="timeline" id="thinking-timeline" role="list">
        ${this.steps.map((step, i) => this._renderStep(step, i))}
      </ul>
    `;
  }

  private _renderStep(step: ThinkingStep, index: number) {
    const isLast = index === this.steps.length - 1;
    const isActive = isLast && this.active;
    // A tool call shows only its monospace detail (tool · args); the
    // "Using <tool>" label would just repeat the tool name. Every other
    // step kind shows its label.
    const toolDetail = step.kind === 'tool_call' && step.args;
    const durationMs = this._stepDurationMs(step, index);
    return html`
      <li class="step ${isActive ? 'active' : ''}">
        ${toolDetail
          ? html`<code class="step-detail">${step.tool ? `${step.tool} · ` : ''}${step.args}</code>`
          : html`<span class="step-label">${step.label}</span>`}
        ${durationMs != null
          ? html`<span class="step-time">${this._formatDuration(durationMs)}</span>`
          : nothing}
      </li>
    `;
  }

  /** `step` mode live render: only the most-recent step as a single line that
   *  replaces in place as the agent advances. Reuses the same label/detail and
   *  live per-step duration logic as the full timeline (so it counts up), minus
   *  the header, chevron, and accumulating list. `role="status"` (implicitly
   *  polite + atomic) matches a single replaced advisory line — the right
   *  semantics for a value that swaps in place, vs. the append-oriented
   *  `role="log"` the full timeline inherits from the message container. */
  private _renderCurrentStep() {
    const index = this.steps.length - 1;
    const step = this.steps[index];
    const toolDetail = step.kind === 'tool_call' && step.args;
    const durationMs = this._stepDurationMs(step, index);
    return html`
      <div class="current-step" role="status">
        ${toolDetail
          ? html`<code class="step-detail">${step.tool ? `${step.tool} · ` : ''}${step.args}</code>`
          : html`<span class="step-label">${step.label}</span>`}
        ${durationMs != null
          ? html`<span class="step-time">${this._formatDuration(durationMs)}</span>`
          : nothing}
      </div>
    `;
  }

  private _toggleExpand(): void {
    this._expanded = !this._expanded;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'steward-thinking': StewardThinking;
  }
}
